import * as PIXI from 'pixi.js';

import type Luminary from './';

type PooledTexture = {
    texture: PIXI.Texture;
    canvas: HTMLCanvasElement;
    holder: object | undefined;
    pixelsDrawnBy: object | undefined;
    detach: () => void;
};

class ReferenceSizedCanvasSource extends PIXI.CanvasSource {
    #logicalWidth = 0;
    #logicalHeight = 0;

    pinLogicalSize(width: number, height: number) {
        this.#logicalWidth = width;
        this.#logicalHeight = height;
        this._resolution = this.pixelWidth / width;
        this.width = width;
        this.height = height;
    }

    override resize(width?: number, height?: number, resolution?: number): boolean {
        const didResize = super.resize(width, height, resolution);
        if (this.#logicalWidth > 0) {
            this.width = this.#logicalWidth;
            this.height = this.#logicalHeight;
        }
        return didResize;
    }
}

export class CanvasReusableTexturesManager {
    #luminary: Luminary;
    #unusedTextures: PooledTexture[] = [];
    #texturesInUse: PooledTexture[] = [];

    constructor(luminary: Luminary) {
        this.#luminary = luminary;
    }

    allocateTexture(detach: () => void, holder?: object): PIXI.Texture {
        const pooled = this.#unusedTextures.pop() ?? this.#createTexture();
        if (holder !== undefined && pooled.pixelsDrawnBy !== holder) {
            const context = pooled.canvas.getContext('2d');
            if (!context) {
                throw new Error('Pooled canvas has been set to a different context mode');
            }
            context.resetTransform();
            context.clearRect(0, 0, pooled.canvas.width, pooled.canvas.height);
            pooled.pixelsDrawnBy = undefined;
        }
        pooled.holder = holder;
        pooled.detach = detach;
        this.#texturesInUse.push(pooled);
        return pooled.texture;
    }

    #createTexture(): PooledTexture {
        const canvas = document.createElement('canvas');
        const canvasResolution = this.#luminary.outputResolution;
        canvas.width = canvasResolution.width;
        canvas.height = canvasResolution.height;
        canvas.style.width = this.#luminary.outputResolution.width + 'px';
        canvas.style.height = this.#luminary.outputResolution.height + 'px';
        const { width: refWidth, height: refHeight } = this.#luminary.referenceResolution;
        const source = new ReferenceSizedCanvasSource({ resource: canvas });
        source.pinLogicalSize(refWidth, refHeight);
        const texture = new PIXI.Texture({ source });
        if (texture.orig === texture.frame) {
            (texture as { orig: PIXI.Rectangle }).orig = texture.frame.clone();
        }
        texture.noFrame = false;
        texture.updateUvs();
        return { texture, canvas, holder: undefined, pixelsDrawnBy: undefined, detach: () => {} };
    }

    detachTextures() {
        const { width: refWidth, height: refHeight } = this.#luminary.referenceResolution;
        for (const pooled of this.#texturesInUse) {
            pooled.detach();
            pooled.pixelsDrawnBy = pooled.holder;
            pooled.holder = undefined;

            // destroy() runs this after assets (and their textures) are torn down; a destroyed
            // texture can't be re-framed or recycled.
            const { texture } = pooled;
            if (texture.destroyed) {
                continue;
            }

            texture.frame.x = 0;
            texture.frame.y = 0;
            texture.frame.width = refWidth;
            texture.frame.height = refHeight;
            texture.orig.x = 0;
            texture.orig.y = 0;
            texture.orig.width = refWidth;
            texture.orig.height = refHeight;
            if (texture.trim) {
                (texture as { trim: PIXI.Rectangle | undefined }).trim = undefined;
            }
            texture.noFrame = false;
            if (texture.source.alphaMode !== 'premultiply-alpha-on-upload') {
                texture.source.alphaMode = 'premultiply-alpha-on-upload';
            }
            this.#unusedTextures.unshift(pooled);
        }
        this.#texturesInUse = [];
    }

    destroy() {
        this.detachTextures();
        for (const pooled of this.#unusedTextures) {
            pooled.texture.destroy(true);
        }

        this.#unusedTextures = [];
    }

    getDebugInfo() {
        return {
            texturesInUse: this.#texturesInUse.length,
            unusedTextures: this.#unusedTextures.length,
        };
    }
}
